import { z } from "zod";
import { TALENT_NETWORK_MONETIZATION_REASONS } from "@artha/types";
import { CandidateEditableProfileSchema } from "./jobs";

export const TALENT_NETWORK_PAGE_SIZE = 20;

export const TalentNetworkCategorySchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  confidence: z.number().min(0).max(1),
});

export const TalentNetworkDistributionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  count: z.number().int().nonnegative(),
  percentage: z.number().min(0).max(100),
});

export const TalentNetworkSummarySchema = z.object({
  professionals: z.number().int().nonnegative(),
  employersHiring: z.number().int().nonnegative().nullable().default(null),
  categories: z.array(TalentNetworkDistributionSchema),
  countries: z.array(TalentNetworkDistributionSchema),
});
export type TalentNetworkSummaryDto = z.infer<
  typeof TalentNetworkSummarySchema
>;

export const TalentNetworkCandidatesQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  monetizable: z.union([
    z.boolean(),
    z.enum(["true", "false"]).transform((value) => value === "true"),
  ]).optional(),
  search: z
    .string()
    .trim()
    .max(100)
    .optional()
    .transform((value) => value || undefined),
});
export type TalentNetworkCandidatesQueryDto = z.infer<
  typeof TalentNetworkCandidatesQuerySchema
>;

export const TalentNetworkMonetizationSchema = z.object({
  monetizable: z.boolean(),
  monetizationReasons: z.array(z.enum(TALENT_NETWORK_MONETIZATION_REASONS)),
});
export type TalentNetworkMonetizationDto = z.infer<typeof TalentNetworkMonetizationSchema>;

export const TalentNetworkCandidateSchema = z.object({
  id: z.string().min(1),
  name: z.string().nullable(),
  // Pending and legacy profiles can contain unreviewed contact text.
  email: z.string().nullable(),
  phone: z.string().nullable(),
  category: z.string().nullable(),
  country: z.string().nullable(),
  ...TalentNetworkMonetizationSchema.shape,
  verificationLevel: z.enum(["dual_otp", "email_only", "phone_only", "legacy_unverified"]),
  joinedAt: z.string(),
});
export type TalentNetworkCandidateDto = z.infer<
  typeof TalentNetworkCandidateSchema
>;

export const TalentNetworkCandidateIdSchema = z.string().regex(/^[a-f\d]{24}$/i);
export const TalentNetworkCandidateDetailsSchema = z.object({
  id: TalentNetworkCandidateIdSchema,
  ...TalentNetworkMonetizationSchema.shape,
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  profile: CandidateEditableProfileSchema.extend({ name: z.string(), email: z.string() }),
});
export type TalentNetworkCandidateDetailsDto = z.infer<typeof TalentNetworkCandidateDetailsSchema>;

export const TalentNetworkCandidatesResponseSchema = z.object({
  items: z.array(TalentNetworkCandidateSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.literal(TALENT_NETWORK_PAGE_SIZE),
  totalPages: z.number().int().nonnegative(),
});
export type TalentNetworkCandidatesResponseDto = z.infer<
  typeof TalentNetworkCandidatesResponseSchema
>;

export const TalentNetworkProfileRequestSchema = z.object({
  externalId: z.string().uuid(),
  posthogDistinctId: z.string().trim().min(1).max(200),
  editableProfile: CandidateEditableProfileSchema,
});
export type TalentNetworkProfileRequestDto = z.infer<
  typeof TalentNetworkProfileRequestSchema
>;

export const TalentNetworkJoinRequestSchema = z.object({
  consent: z.literal(true),
}).strict();

export const TalentNetworkPhoneRequestSchema = z.object({
  externalId: z.string().uuid(),
  phone: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/, "Use an E.164 phone number"),
});

export const TalentNetworkPhoneVerifySchema =
  TalentNetworkPhoneRequestSchema.extend({
    code: z.string().regex(/^\d{4,10}$/, "Enter the verification code"),
  });

export const TalentNetworkSessionSchema = z.object({
  authenticated: z.boolean(),
  emailVerified: z.boolean().optional(),
  externalId: z.string().uuid().optional(),
  membershipStatus: z
    .enum(["pending_verification", "active", "left"])
    .optional(),
  verificationLevel: z
    .enum(["dual_otp", "email_only", "phone_only", "legacy_unverified"])
    .optional(),
  editableProfile: CandidateEditableProfileSchema.optional(),
});
export type TalentNetworkSessionDto = z.infer<
  typeof TalentNetworkSessionSchema
>;

export const TalentNetworkJoinResponseSchema = TalentNetworkSessionSchema.extend({
  authenticated: z.literal(true),
  membershipStatus: z.literal("active"),
});
